import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { FLOAT_DEFAULT, FLOAT_MAX, FLOAT_MIN, floatError, SENSOR_TEXT, sensorText } from "./fleet";
import { type Saved, saveFailure } from "./save";

const TYPES = Object.keys(SENSOR_TEXT) as api.SensorType[];

/** Roadmap 062: the sensor of one container, with a form in its place to change it. */
export function ContainerSensor({
  accountId,
  container,
  canEdit,
  user,
  onSaved,
}: {
  accountId: string;
  container: api.Container;
  canEdit: boolean;
  user: User;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [type, setType] = useState<api.SensorType>(container.sensor_type);
  const [pct, setPct] = useState(String(container.float_pct));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<Saved | null>(null);
  const select = useRef<HTMLSelectElement>(null);
  const change = useRef<HTMLButtonElement>(null);
  const id = `sensor-${container.id}`;
  useEffect(() => {
    if (editing) select.current?.focus();
  }, [editing]);

  const close = () => {
    setEditing(false);
    requestAnimationFrame(() => change.current?.focus());
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = type === "float" ? floatError(pct) : null;
    if (problem) {
      setSaved({ ok: false, field: true, text: problem });
      return;
    }
    setSaving(true);
    setSaved(null);
    try {
      const body: api.SensorChange = type === "float" ? { sensor_type: type, float_pct: Number(pct) } : { sensor_type: type };
      const next = await api.setContainerSensor(await user.getIdToken(), accountId, container.id, body);
      onSaved(next);
      const shown = next.containers.find((found) => found.id === container.id) ?? container;
      setSaved({ ok: true, text: `Saved: ${sensorText(shown)}.` });
      close();
    } catch (e) {
      setSaved(saveFailure(e, "The sensor"));
    } finally {
      setSaving(false);
    }
  };
  if (!editing) {
    return (
      <span className="sensor" data-testid="container-sensor" data-sensor={container.sensor_type}>
        {sensorText(container)}
        {canEdit && (
          <>
            {" "}
            <button
              ref={change}
              type="button"
              className="quiet"
              data-testid="sensor-change"
              aria-label={`Change the sensor of the ${container.size_gal} gal ${container.kind}`}
              onClick={() => {
                setType(container.sensor_type);
                setPct(String(container.float_pct));
                setSaved(null);
                setEditing(true);
              }}
            >
              Change
            </button>
          </>
        )}
        {saved && (
          <span role="status" data-testid="sensor-status" className={saved.ok ? "sub" : "sub error"}>
            {saved.text}
          </span>
        )}
      </span>
    );
  }
  const wrong = Boolean(saved && !saved.ok && saved.field);
  return (
    <form className="sensor-form" data-testid="sensor-form" onSubmit={(e) => void submit(e)} noValidate>
      <label htmlFor={`${id}-type`}>Sensor</label>
      <select id={`${id}-type`} ref={select} data-testid="sensor-type" value={type} onChange={(e) => setType(e.target.value as api.SensorType)}>
        {TYPES.map((value) => (
          <option key={value} value={value}>
            {SENSOR_TEXT[value]}
          </option>
        ))}
      </select>
      {type === "float" && (
        <>
          <label htmlFor={`${id}-pct`}>Float height (% of the container)</label>
          <input
            id={`${id}-pct`}
            data-testid="sensor-float"
            inputMode="numeric"
            value={pct}
            aria-invalid={wrong || undefined}
            aria-describedby={`${id}-hint`}
            onChange={(e) => {
              setPct(e.target.value);
              setSaved(null);
            }}
          />
          <p id={`${id}-hint`} className="hint">
            From {FLOAT_MIN} to {FLOAT_MAX}. The standard height is {FLOAT_DEFAULT}%. The switch reads full when the oil is above the float.
          </p>
        </>
      )}
      {saved && !saved.ok && (
        <p role="alert" className="error" data-testid="sensor-error">
          {saved.text}
        </p>
      )}
      <div className="actions">
        <button className="primary" data-testid="sensor-save" disabled={saving}>
          {saving ? "Saving…" : "Save the sensor"}
        </button>
        <button type="button" onClick={close} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}
